/**
 * icons.js — the system icon set.
 *
 * Owns: the SVG path data for StudyMeneer's application/interface icons, and
 *       the one function that turns a `data-icon` placeholder into an <svg>.
 * Does not own: which icon a screen uses, icon colour (that is CSS), layout,
 *       persistence, or anything a student can see change.
 *
 * ── Why the path data lives here instead of in a package ──────────────────────
 *
 * These are Lucide icons (https://lucide.dev, ISC licence), transcribed. The
 * obvious alternative — `lucide-react` — needs React, and there is none here;
 * the next one — the UMD build from a CDN — needs a network on first paint, and
 * offline-first is the product, not a feature (AGENTS.md §2). A third-party
 * script tag would also have to be precached, CSP-listed and version-pinned to
 * buy us thirteen icons out of ~1,600.
 *
 * So the icons are inlined. Zero dependencies, zero build step, zero network,
 * and the whole set costs less than one of the emoji fonts it replaces. Adding
 * an icon means copying its paths from lucide.dev into ICONS below — keep the
 * name Lucide uses, so the source is always one search away.
 *
 * ── Semantic map (one meaning, one name) ─────────────────────────────────────
 *
 * Keep this table honest. A second picture for the same action is how Study
 * and Read both ended up as `book-open`.
 *
 *   Home              house
 *   Plan / tasks      list-todo
 *   Study (nav)       book-open
 *   Session / timer   timer
 *   Meneer            sparkles
 *   Library           folder-open
 *   Notes             file-text
 *   PDF               file
 *   Quizzes           circle-help
 *   Flashcards        layers
 *   AI Quiz           sparkles
 *   Schedule          calendar-days
 *   Settings          settings
 *   Music             music
 *   Focus             target
 *   Lecture           mic
 *   Read a note       book
 *   Study a deck      book-open
 *   Search            search
 *   Fullscreen        maximize
 *   Send              send
 *   Park a thought    brain
 *   Take a breather   leaf
 *   Edit              pencil
 *   Delete            trash-2
 *   Play / Start      play
 *   Pause             pause
 *   Generate (AI)     sparkles
 *   Streak            flame
 *   Appearance        palette
 *   Account           user
 *   Notifications     bell
 *   Billing           credit-card
 *
 * ── Why hydration and not literal <svg> in the markup ────────────────────────
 *
 * Because the same icon appears on more than one surface, and a copy per use is
 * a copy that drifts. A placeholder is one attribute:
 *
 *     <span class="sm-icon-tile" data-icon="flame"></span>
 *
 * Decorative by default: the <svg> is written with aria-hidden="true", because
 * an icon next to the words "Study Streak" tells a screen-reader user nothing
 * they are not already being told. Give the placeholder a `data-icon-label` and
 * it becomes role="img" with that accessible name instead — for the icon-only
 * buttons in the floating dock, where the icon IS the label.
 */

/** viewBox is 24x24 for every Lucide icon; only the inner paths differ. */
const ICONS = {
    'flame':
        '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>',
    'file-text':
        '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>' +
        '<path d="M14 2v4a2 2 0 0 0 2 2h4"/>' +
        '<path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
    'bar-chart-3':
        '<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
    'clock':
        '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
    'timer':
        '<line x1="10" x2="14" y1="2" y2="2"/><line x1="12" x2="15" y1="14" y2="11"/>' +
        '<circle cx="12" cy="14" r="8"/>',
    'calendar-days':
        '<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/>' +
        '<path d="M3 10h18"/><path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/>' +
        '<path d="M8 18h.01"/><path d="M12 18h.01"/><path d="M16 18h.01"/>',
    'calendar-clock':
        '<path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5"/>' +
        '<path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h5"/>' +
        '<path d="M17.5 17.5 16 16.3V14"/><circle cx="16" cy="16" r="6"/>',
    'list-todo':
        '<rect x="3" y="5" width="6" height="6" rx="1"/><path d="m3 17 2 2 4-4"/>' +
        '<path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/>',
    'history':
        '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>' +
        '<path d="M12 7v5l4 2"/>',
    'sparkles':
        '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 ' +
        '9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 ' +
        '1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>' +
        '<path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>',
    'music':
        '<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>',
    'mic':
        '<path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>' +
        '<path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>',
    'book-open':
        '<path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 ' +
        '4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/>',
    'clipboard-list':
        '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>' +
        '<path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>' +
        '<path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>',
    'circle-check-big':
        '<path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/>',
    'triangle-alert':
        '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/>' +
        '<path d="M12 9v4"/><path d="M12 17h.01"/>',
    'info':
        '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
    // The three task energy levels. They are a set: deep / medium / light.
    'brain':
        '<path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>' +
        '<path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>' +
        '<path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>' +
        '<path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/>' +
        '<path d="M3.477 10.896a4 4 0 0 1 .585-.396"/><path d="M19.938 10.5a4 4 0 0 1 .585.396"/>' +
        '<path d="M6 18a4 4 0 0 1-1.967-.516"/><path d="M19.967 17.484A4 4 0 0 1 18 18"/>',
    'zap':
        '<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 ' +
        '10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>',
    'leaf':
        '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>' +
        '<path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>',
    'arrow-up-down':
        '<path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/>',
    'pencil':
        '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 ' +
        '0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/>',
    // ── The note editor's toolbar ────────────────────────────────────────────
    'link':
        '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>' +
        '<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    'image':
        '<rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/>' +
        '<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>',
    'workflow':
        '<rect width="8" height="8" x="3" y="3" rx="2"/>' +
        '<path d="M7 11v4a2 2 0 0 0 2 2h4"/><rect width="8" height="8" x="13" y="13" rx="2"/>',
    'align-left':
        '<path d="M15 12H3"/><path d="M17 18H3"/><path d="M21 6H3"/>',
    'align-center':
        '<path d="M17 12H7"/><path d="M19 18H5"/><path d="M21 6H3"/>',
    'align-right':
        '<path d="M21 12H9"/><path d="M21 18H7"/><path d="M21 6H3"/>',
    'align-justify':
        '<path d="M3 12h18"/><path d="M3 18h18"/><path d="M3 6h18"/>',
    'indent-increase':
        '<path d="m3 8 4 4-4 4"/><path d="M21 12H11"/><path d="M21 18H11"/><path d="M21 6H11"/>',
    'indent-decrease':
        '<path d="m7 8-4 4 4 4"/><path d="M21 12H11"/><path d="M21 18H11"/><path d="M21 6H11"/>',
    'circle-help':
        '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>' +
        '<path d="M12 17h.01"/>',
    'check':
        '<path d="M20 6 9 17l-5-5"/>',
    'search':
        '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
    'eye':
        '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>' +
        '<circle cx="12" cy="12" r="3"/>',
    'eye-off':
        '<path d="m15 18-.722-3.25"/><path d="M2 8a10.645 10.645 0 0 0 20 0"/>' +
        '<path d="m20 15-1.726-2.05"/><path d="m4 15 1.726-2.05"/><path d="m9 18 .722-3.25"/>',
    'maximize':
        '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>' +
        '<path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>',
    'lightbulb':
        '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 ' +
        '3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>',
    'save':
        '<path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>' +
        '<path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/>',
    // Spins via .sm-icon--spin in style.css. An arc that does not rotate reads as
    // a broken circle, not as progress.
    'loader-circle':
        '<path d="M21 12a9 9 0 1 1-6.219-8.56"/>',
    'arrow-left':
        '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>',
    'arrow-left-right':
        '<path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>',
    'panel-left':
        '<rect width="18" height="18" x="3" y="3" rx="2"/><path d="M9 3v18"/>',
    'play':
        '<path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/>',
    'chevron-down':
        '<path d="m6 9 6 6 6-6"/>',
    'chevron-up':
        '<path d="m18 15-6-6-6 6"/>',
    'trash-2':
        '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>' +
        '<path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>' +
        '<line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>',
    // ── Shell, screens, and controls that used to be emoji ───────────────────
    'house':
        '<path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>' +
        '<path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
    'folder-open':
        '<path d="m6 14 1.45-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"/>',
    'file':
        '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>' +
        '<path d="M14 2v4a2 2 0 0 0 2 2h4"/>',
    'layers':
        '<path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/>' +
        '<path d="m22 12.43-8.58 3.91a2 2 0 0 1-1.66 0L2.6 12.43"/>' +
        '<path d="m22 17.57-8.58 3.91a2 2 0 0 1-1.66 0L2.6 17.57"/>',
    'target':
        '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
    'settings':
        '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>' +
        '<circle cx="12" cy="12" r="3"/>',
    'pause':
        '<rect x="14" y="3" width="5" height="18" rx="1"/><rect x="5" y="3" width="5" height="18" rx="1"/>',
    'square':
        '<rect width="18" height="18" x="3" y="3" rx="2"/>',
    'shuffle':
        '<path d="m18 14 4 4-4 4"/><path d="m18 2 4 4-4 4"/>' +
        '<path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6A4 4 0 0 1 16.027 6H20"/>' +
        '<path d="M2 6h1.972a4 4 0 0 1 3.3 1.7l.027.04"/>' +
        '<path d="M20 18h-3.973a4 4 0 0 1-3.3-1.7l-.027-.04"/>',
    'trophy':
        '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>' +
        '<path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>' +
        '<path d="M14 14.66V17c0 .47.43.89.97 1.21C16.15 18.75 17 20.24 17 22"/>' +
        '<path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>',
    'upload':
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
        '<polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/>',
    'refresh-cw':
        '<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>' +
        '<path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>' +
        '<path d="M8 16H3v5"/>',
    'coffee':
        '<path d="M10 2v2"/><path d="M14 2v2"/><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1"/><path d="M6 2v2"/>',
    'globe':
        '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
    'database':
        '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>',
    'book':
        '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>',
    'chevron-left':
        '<path d="m15 18-6-6 6-6"/>',
    'chevron-right':
        '<path d="m9 18 6-6-6-6"/>',
    'plus':
        '<path d="M5 12h14"/><path d="M12 5v14"/>',
    'x':
        '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    'circle-x':
        '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
    'thumbs-up':
        '<path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>',
    'rotate-ccw':
        '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
    'frown':
        '<circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><path d="M9 9h.01"/><path d="M15 9h.01"/>',
    'moon':
        '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
    'send':
        '<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>',
    'map-pin':
        '<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>' +
        '<circle cx="12" cy="10" r="3"/>',
    'palette':
        '<circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>' +
        '<circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>' +
        '<circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>' +
        '<circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>' +
        '<path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>',
    'user':
        '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>' +
        '<circle cx="12" cy="7" r="4"/>',
    'bell':
        '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>' +
        '<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
    'credit-card':
        '<rect width="20" height="14" x="2" y="5" rx="2"/>' +
        '<line x1="2" x2="22" y1="10" y2="10"/>',
    'graduation-cap':
        '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/>' +
        '<path d="M22 10v6"/>' +
        '<path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>',
    'message-circle':
        '<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>',
};

const DEFAULT_SIZE = 20;

/**
 * One icon, as an SVG string.
 *
 * `currentColor` on the stroke is deliberate and is the whole colour contract:
 * every accent in this app is set on the CONTAINER in CSS, never here, so an
 * icon can be re-tinted without anyone editing JavaScript. See the
 * `--icon-*` tokens in style.css.
 *
 * @param {string} name  a key of ICONS
 * @param {{size?: number, className?: string, label?: string}} [opts]
 * @returns {string} SVG markup, or '' for an unknown name
 */
export function iconSvg(name, opts = {}) {
    const paths = ICONS[name];
    if (!paths) {
        console.warn(`[Icons] unknown icon "${name}"`);
        return '';
    }
    const size  = opts.size || DEFAULT_SIZE;
    const cls   = opts.className ? `sm-icon ${opts.className}` : 'sm-icon';
    // An icon with a name is content; one without is decoration. There is no
    // third case, and getting it wrong in either direction is an a11y bug:
    // an unlabelled icon-only button is unusable, and a labelled decorative
    // icon makes a screen reader announce "Study Streak" twice.
    const a11y  = opts.label
        ? `role="img" aria-label="${String(opts.label).replace(/"/g, '&quot;')}"`
        : 'aria-hidden="true"';
    return `<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
           `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ` +
           `stroke-linejoin="round" ${a11y}>${paths}</svg>`;
}

/**
 * Replace every `data-icon` placeholder under `root` with its SVG.
 *
 * Idempotent by design — it marks what it has done with `data-icon-ready` and
 * skips it next time. `loadDashboardData()` runs on every navigation back to
 * the dashboard, and re-rendering the same twelve SVGs on each one would be
 * pure waste.
 *
 * @param {ParentNode} [root=document]
 * @returns {number} how many placeholders were filled
 */
export function hydrateIcons(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return 0;
    let n = 0;
    for (const el of root.querySelectorAll('[data-icon]:not([data-icon-ready])')) {
        const svg = iconSvg(el.dataset.icon, {
            size:  Number(el.dataset.iconSize) || DEFAULT_SIZE,
            label: el.dataset.iconLabel,
        });
        if (!svg) continue;
        el.innerHTML = svg;
        el.setAttribute('data-icon-ready', '');
        n++;
    }
    return n;
}

/**
 * Put a Lucide icon and a text label on a button without destroying a child
 * the mobile CSS targets (AGENTS.md SKILL 6). Call this instead of assigning
 * `textContent` onto the button itself.
 *
 * @param {Element|null} btn
 * @param {string} name
 * @param {string} label
 * @param {{size?: number}} [opts]
 */
export function setButtonChrome(btn, name, label, opts = {}) {
    if (!btn) return;
    const size = opts.size || 14;
    const cls  = opts.className || 'sm-icon--inline';
    const canQuery = typeof btn.querySelector === 'function';
    let slot = canQuery ? btn.querySelector('[data-icon-label-slot]') : null;
    const markup = iconSvg(name, { size, className: cls });
    if (slot) {
        const existing = canQuery ? btn.querySelector(':scope > svg.sm-icon') : null;
        if (existing) existing.outerHTML = markup;
        else if (typeof btn.insertAdjacentHTML === 'function') btn.insertAdjacentHTML('afterbegin', markup);
        slot.textContent = label;
        return;
    }
    if (canQuery) {
        btn.innerHTML = `${markup}<span data-icon-label-slot></span>`;
        slot = btn.querySelector('[data-icon-label-slot]');
        if (slot) {
            slot.textContent = label;
            return;
        }
    }
    // Test stubs and any element that cannot host children: the words still land.
    btn.textContent = label;
}

/**
 * Strip a leading pictograph from a toast or status string. Toasts already
 * carry a Lucide icon by type; a second glyph in the message is leftover from
 * when emoji was the icon system.
 *
 * @param {string} text
 * @returns {string}
 */
export function stripLeadingGlyphs(text) {
    let s = String(text);
    const leading = /^(?:(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})(?:\uFE0F|\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}))*|[▶⏸⚡⏱✨☑☐⏎↩🍅])+\s*/u;
    for (let i = 0; i < 4; i++) {
        const next = s.replace(leading, '');
        if (next === s) break;
        s = next;
    }
    return s;
}

/** Every name this module knows. For tests and for diagnostics. */
export const ICON_NAMES = Object.keys(ICONS);

const Icons = { svg: iconSvg, hydrate: hydrateIcons, setButtonChrome, stripLeadingGlyphs, names: ICON_NAMES };

export default Icons;

// Transition shim (AGENTS.md SKILL 1) — markup-owning modules that were never
// converted reach this bare.
if (typeof window !== 'undefined') {
    window.Icons = Icons;
    const doc = typeof document !== 'undefined' ? document : null;
    if (doc) {
        if (doc.readyState === 'loading') {
            doc.addEventListener('DOMContentLoaded', () => hydrateIcons(doc), { once: true });
        } else {
            hydrateIcons(doc);
        }
    }
}
